import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, Download, Eye, HelpCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface UploadSectionProps {
  onFileSelect: (file: File) => void;
  onDownloadTemplate: () => void;
  onViewSampleData?: () => void;
  onRetry?: () => void;
  acceptedTypes?: string[];
  maxSizeMB?: number;
  disabled?: boolean;
  isProcessing?: boolean;
  processingError?: string | null;
}

/**
 * UploadSection - Compact upload zone for 100% screen fit
 */
export default function UploadSection({
  onFileSelect,
  onDownloadTemplate,
  onViewSampleData,
  onRetry,
  acceptedTypes = ['.csv', '.xlsx', '.xls'],
  maxSizeMB = 100,
  disabled = false,
  isProcessing = false,
  processingError = null,
}: UploadSectionProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedTypes.includes(fileExtension)) {
      return `File type not supported. Please upload: ${acceptedTypes.join(', ')}`;
    }
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      return `File size exceeds ${maxSizeMB}MB limit`;
    }
    return null;
  };

  const handleFile = useCallback(
    (file: File) => {
      setError(null);
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }
      onFileSelect(file);
    },
    [onFileSelect, acceptedTypes, maxSizeMB]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled || isProcessing) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile, disabled, isProcessing]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled && !isProcessing) setIsDragging(true);
  }, [disabled, isProcessing]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const openFilePicker = () => {
    if (disabled || isProcessing) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptedTypes.join(',');
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) handleFile(file);
    };
    input.click();
  };

  const displayError = error || processingError;

  return (
    <div className="px-3 py-2 space-y-1.5">
      {/* Drop Zone - Ultra Compact */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'border border-dashed rounded-md p-2 text-center transition-all duration-200',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && 'border-border hover:border-primary/50 hover:bg-muted/30',
          (disabled || isProcessing) && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center gap-2 py-1">
            <RefreshCw className="h-4 w-4 text-primary animate-spin" />
            <p className="text-xs font-medium">Processing...</p>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3">
            <div className={cn(
              'h-8 w-8 rounded-full flex items-center justify-center shrink-0',
              isDragging ? 'bg-primary/20' : 'bg-muted'
            )}>
              <Upload className={cn(
                'h-4 w-4',
                isDragging ? 'text-primary' : 'text-muted-foreground'
              )} />
            </div>
            
            <div className="text-left">
              <p className="text-xs">
                <button onClick={openFilePicker} className="text-primary font-medium hover:underline">Browse</button>
                {' '}or drag & drop
              </p>
              <p className="text-[10px] text-muted-foreground">
                CSV, XLSX • Max {maxSizeMB}MB
              </p>
            </div>
            
            <Button variant="outline" size="sm" onClick={onDownloadTemplate} className="h-7 text-xs gap-1 ml-auto">
              <Download className="h-3 w-3" />
              Template
            </Button>
          </div>
        )}
      </div>

      {/* Error with Retry */}
      {displayError && (
        <div className="flex items-center justify-between text-[10px] text-destructive bg-destructive/10 px-2 py-1.5 rounded">
          <span>{displayError}</span>
          <div className="flex gap-1">
            {onRetry && (
              <Button variant="outline" size="sm" className="h-5 px-2 text-[10px]" onClick={onRetry}>
                <RefreshCw className="h-2.5 w-2.5 mr-1" />
                Retry
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
